// ===========================================
// HOOKS: Tareas recurrentes (CORE) — ORR-304
// ===========================================

import { useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { toast } from 'sonner'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { usePermissions } from '@/hooks/usePermissions'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type { Permisos } from '@/types/roles'
import { assertPermiso, handleMutationError, PermisoError, puedeTocarTarea } from '@/lib/permisos-guard'
import {
  limiteDeAnticipacion,
  tareasRecurrentesService,
  textoAnticipacion,
  type TareaRecurrente,
  type TareaRecurrenteInput,
} from '../services/tareasRecurrentesService'
import { tieneGrupo } from '../services/tareasGrupoService'
import { tareasKeys } from './useTareas'

export const tareasRecurrentesKeys = {
  all: ['tareas-recurrentes'] as const,
  lista: (organizacionId?: string) => [...tareasRecurrentesKeys.all, 'lista', organizacionId] as const,
  anticipacion: () => [...tareasRecurrentesKeys.all, 'anticipacion'] as const,
}

export function useTareasRecurrentes() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()

  return useQuery({
    queryKey: tareasRecurrentesKeys.lista(organizacionId ?? undefined),
    queryFn: () => tareasRecurrentesService.listar(supabase!, organizacionId!),
    enabled: !!supabase && !!organizacionId,
  })
}

/** Días de anticipación con que el cron crea las tareas de una repetición. */
export function useAnticipacionRecurrentes() {
  const { supabase } = useSupabase()

  return useQuery({
    queryKey: tareasRecurrentesKeys.anticipacion(),
    queryFn: () => tareasRecurrentesService.obtenerAnticipacionDias(supabase!),
    enabled: !!supabase,
  })
}

export function useCrearTareaRecurrente() {
  const { supabase } = useSupabase()
  const { organizacionId } = useOrganizacion()
  const { user } = useAuth0()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()
  // Id de la repetición del alta en curso. Un reintento con los mismos datos reusa el id, así que
  // si el INSERT anterior se guardó sin que llegara la respuesta, el reintento lee esa fila en vez
  // de crear otra. Datos distintos son otra alta y llevan otro id. Se descarta al guardar.
  const altaEnCurso = useRef<{ datos: string; id: string } | null>(null)

  return useMutation({
    mutationFn: async (
      datos: Omit<TareaRecurrenteInput, 'organizacion_id' | 'created_by'>
    ): Promise<{
      recurrente: TareaRecurrente
      creadas: number | null
      anticipacionDias: number
    }> => {
      // Una recurrente es una plantilla de tarea: usa los permisos de tareas, no
      // unos propios.
      assertPermiso(puede, 'crearTarea')

      // Antes de guardar: la lectura nunca rechaza, y si rechazara no dejaría una repetición
      // guardada con la mutación fallida.
      const anticipacionDias = await queryClient.fetchQuery({
        queryKey: tareasRecurrentesKeys.anticipacion(),
        queryFn: () => tareasRecurrentesService.obtenerAnticipacionDias(supabase!),
      })

      const clave = JSON.stringify(datos)
      if (altaEnCurso.current?.datos !== clave) {
        altaEnCurso.current = { datos: clave, id: crypto.randomUUID() }
      }

      const recurrente = await tareasRecurrentesService.crear(supabase!, {
        ...datos,
        id: altaEnCurso.current.id,
        organizacion_id: organizacionId!,
        created_by: user?.email ?? '',
      })
      altaEnCurso.current = null

      // Con la repetición ya guardada, un fallo de la primera ocurrencia no hace fallar la
      // mutación: un reintento guardaría otra repetición igual. `null` significa que la RPC falló,
      // y las tareas las crea el cron en su próxima corrida.
      const creadas = await tareasRecurrentesService
        .generarPrimeraOcurrencia(supabase!, recurrente.id)
        .catch((error: unknown) => {
          console.error('Error creando la primera ocurrencia de la repetición:', error)
          return null
        })
      return { recurrente, creadas, anticipacionDias }
    },
    onSuccess: ({ recurrente, creadas, anticipacionDias }) => {
      void queryClient.invalidateQueries({ queryKey: tareasRecurrentesKeys.all })

      if (creadas === null) {
        toast.warning('Repetición guardada. Sus tareas no se pudieron crear ahora: se vuelve a intentar a las 6 de la mañana.')
        return
      }

      if (creadas > 0) {
        void queryClient.invalidateQueries({ queryKey: tareasKeys.all })
        toast.success(
          creadas === 1
            ? 'Tarea creada. Se va a repetir según la regla que elegiste.'
            : tieneGrupo(recurrente)
              ? `${creadas} tareas creadas, una por cliente. Se repiten según la regla que elegiste.`
              : `${creadas} tareas creadas. Se repiten según la regla que elegiste.`
        )
        return
      }

      // Grupo sin clientes activos con la ocurrencia dentro de la anticipación.
      if (
        tieneGrupo(recurrente) &&
        recurrente.proxima_fecha &&
        recurrente.proxima_fecha <= limiteDeAnticipacion(anticipacionDias)
      ) {
        toast.success(
          'Repetición guardada. Ahora no se creó ninguna tarea; el cron vuelve a revisar el grupo a las 6 de la mañana.'
        )
        return
      }

      toast.success(
        recurrente.proxima_fecha
          ? `Repetición guardada. La primera tarea vence el ${formatearFecha(recurrente.proxima_fecha)} y aparece en el panel ${textoAnticipacion(anticipacionDias)}.`
          : 'Repetición guardada.'
      )
    },
    onError: (error) => handleMutationError(error, 'No se pudo guardar la repetición'),
  })
}

/**
 * Rechaza si el usuario no puede tocar la repetición, con la misma regla que una tarea:
 * `editarTarea`, o `editarTareaPropia` si la repetición es suya. Sin `editarTarea` la fila se
 * lee de la base: el caché puede traer una asignación vieja.
 */
const assertPuedeTocarRecurrente = async (
  supabase: SupabaseClient<Database>,
  puede: Partial<Permisos>,
  emailUsuario: string | null | undefined,
  id: string
): Promise<void> => {
  if (puede.editarTarea) return
  assertPermiso(puede, 'editarTareaPropia')
  const recurrente = await tareasRecurrentesService.obtener(supabase, id)
  if (!puedeTocarTarea(recurrente, puede, emailUsuario)) throw new PermisoError('editarTareaPropia')
}

export function useActualizarTareaRecurrente() {
  const { supabase } = useSupabase()
  const { puede, emailUsuario } = usePermissions()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, cambios }: { id: string; cambios: Partial<TareaRecurrenteInput> }) => {
      await assertPuedeTocarRecurrente(supabase!, puede, emailUsuario, id)
      return tareasRecurrentesService.actualizar(supabase!, id, cambios)
    },
    onSuccess: (recurrente) => {
      void queryClient.invalidateQueries({ queryKey: tareasRecurrentesKeys.all })
      toast.success(
        recurrente.activa && recurrente.proxima_fecha
          ? `Repetición actualizada. La próxima tarea vence el ${formatearFecha(recurrente.proxima_fecha)}.`
          : 'Repetición actualizada.'
      )
    },
    onError: (error) => handleMutationError(error, 'No se pudo actualizar la repetición'),
  })
}

export function usePausarTareaRecurrente() {
  const { supabase } = useSupabase()
  const { puede, emailUsuario } = usePermissions()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, activa }: { id: string; activa: boolean }) => {
      await assertPuedeTocarRecurrente(supabase!, puede, emailUsuario, id)
      return tareasRecurrentesService.pausar(supabase!, id, activa)
    },
    onSuccess: (recurrente) => {
      void queryClient.invalidateQueries({ queryKey: tareasRecurrentesKeys.all })
      toast.success(
        recurrente.activa
          ? 'Repetición reactivada: vuelve a generar tareas'
          : 'Repetición pausada: no genera tareas nuevas'
      )
    },
    onError: (error) => handleMutationError(error, 'No se pudo cambiar el estado'),
  })
}

export function useEliminarTareaRecurrente() {
  const { supabase } = useSupabase()
  const { puede } = usePermissions()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => {
      assertPermiso(puede, 'eliminarTarea')
      return tareasRecurrentesService.eliminar(supabase!, id)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tareasRecurrentesKeys.all })
      // Las tareas ya generadas quedan: borrar la regla no borra el trabajo hecho.
      toast.success('Repetición eliminada. Las tareas ya creadas siguen en el panel.')
    },
    onError: (error) => handleMutationError(error, 'No se pudo eliminar la repetición'),
  })
}

const formatearFecha = (iso: string): string => {
  const [anio, mes, dia] = iso.split('-')
  return `${dia}/${mes}/${anio}`
}
